import {
  CheckinRequestSchema,
  collapseWhitespace,
  normalizeVietnamPhone,
  errorMessageVi,
  type CheckinRequest,
  type FormField,
} from '@irace/shared';
import type { SubmitOutcome } from '../api/client.ts';
import type { OutboxStore } from '../db/indexeddb.ts';
import {
  isRetryableErrorCode,
  OUTBOX_SCHEMA_VERSION,
  type OutboxData,
  type OutboxRecord,
} from '../db/outbox-record.ts';
import type { TurnstileExecutor } from '../turnstile/client.ts';

/**
 * G-002 — save-before-network submission service (§8.1, §12).
 *
 * The order here is the whole point and is enforced by structure, not by
 * comment: `save()` cannot reach the network, and `send()` cannot be called
 * with anything but an already-persisted record.
 */

/** Field keys the API models as first-class columns; the rest become payload. */
const FULL_NAME_KEY = 'full_name';
const PHONE_KEY = 'phone';
const EMAIL_KEY = 'email';

export class LocalSaveError extends Error {
  constructor(cause: unknown) {
    super('outbox_write_failed', { cause });
    this.name = 'LocalSaveError';
  }
}

export interface SubmissionInput {
  eventId: string;
  eventConfigVersion: number;
  locationId: string;
  locationName: string;
  fields: readonly FormField[];
  values: Record<string, string>;
}

/**
 * What a send attempt did to the record. The UI maps these one-to-one onto
 * M04–M07, so a new outcome cannot be added without deciding what the
 * participant sees.
 */
export type SendResult =
  | { kind: 'accepted'; record: OutboxRecord }
  | { kind: 'pending'; record: OutboxRecord; retryAfterSeconds?: number }
  | { kind: 'verify_retry'; record: OutboxRecord }
  | { kind: 'rejected'; record: OutboxRecord; code: string; message: string };

export interface SubmissionServiceDeps {
  outbox: OutboxStore;
  turnstile: TurnstileExecutor;
  submit: (request: CheckinRequest) => Promise<SubmitOutcome>;
  now?: () => Date;
  newSubmissionId?: () => string;
  isOnline?: () => boolean;
}

/** Split flat form values into the transport shape (§28.4 fields are config-driven). */
export function toOutboxData(
  fields: readonly FormField[],
  values: Record<string, string>,
): OutboxData {
  const payload: Record<string, unknown> = {};
  let fullName = '';
  let phone: string | undefined;
  let email: string | undefined;

  for (const field of fields) {
    const collapsed = collapseWhitespace(values[field.key] ?? '');
    if (collapsed.length === 0) continue;

    // A phone number is saved the way it will be validated and reported:
    // digits, leading zero. Storing "090 123 4567" would put the same person
    // in the organizer's export under two different spellings.
    const value = field.type === 'tel' ? normalizeVietnamPhone(collapsed) : collapsed;

    if (field.key === FULL_NAME_KEY) fullName = value;
    else if (field.key === PHONE_KEY) phone = value;
    else if (field.key === EMAIL_KEY) email = value;
    else payload[field.key] = value;
  }

  return {
    fullName,
    ...(phone !== undefined ? { phone } : {}),
    ...(email !== undefined ? { email } : {}),
    ...(Object.keys(payload).length > 0 ? { payload } : {}),
  };
}

/** Build the wire request. The token lives only in this object, in memory. */
export function toCheckinRequest(record: OutboxRecord, turnstileToken: string): CheckinRequest {
  return {
    submission_id: record.submissionId,
    event_id: record.eventId,
    event_config_version: record.eventConfigVersion,
    location_id: record.locationId,
    client_created_at: record.clientCreatedAt,
    turnstile_token: turnstileToken,
    data: {
      full_name: record.data.fullName,
      ...(record.data.phone !== undefined ? { phone: record.data.phone } : {}),
      ...(record.data.email !== undefined ? { email: record.data.email } : {}),
      ...(record.data.payload !== undefined ? { payload: record.data.payload } : {}),
    },
  };
}

export class SubmissionService {
  readonly #deps: Required<Omit<SubmissionServiceDeps, 'outbox' | 'turnstile' | 'submit'>> &
    SubmissionServiceDeps;

  constructor(deps: SubmissionServiceDeps) {
    this.#deps = {
      ...deps,
      now: deps.now ?? (() => new Date()),
      newSubmissionId: deps.newSubmissionId ?? (() => crypto.randomUUID()),
      isOnline: deps.isOnline ?? (() => navigator.onLine),
    };
  }

  /**
   * Step A–C of §12: new UUID, snapshot, durable write. Nothing touches the
   * network here, so a failure to persist can still stop the submission — a
   * send whose record was never written would be invisible on reload.
   */
  async save(input: SubmissionInput): Promise<OutboxRecord> {
    const record: OutboxRecord = {
      schemaVersion: OUTBOX_SCHEMA_VERSION,
      submissionId: this.#deps.newSubmissionId(),
      eventId: input.eventId,
      eventConfigVersion: input.eventConfigVersion,
      locationId: input.locationId,
      locationName: input.locationName,
      data: toOutboxData(input.fields, input.values),
      clientCreatedAt: this.#deps.now().toISOString(),
      status: 'pending',
      attemptCount: 0,
    };

    try {
      await this.#deps.outbox.putPending(record);
    } catch (error) {
      throw new LocalSaveError(error);
    }

    return record;
  }

  /**
   * Step D: Turnstile, then POST. Always called with a record that is already
   * on disk, and always with the record's original submission_id — a retry
   * never mints a new one (AGENTS.md invariant 4).
   */
  async send(record: OutboxRecord): Promise<SendResult> {
    if (!this.#deps.isOnline()) {
      // Nothing to mark: the record is already pending, and burning an attempt
      // on a device with no radio would only inflate the backoff.
      return { kind: 'pending', record };
    }

    const sending = (await this.#deps.outbox.markSending(record.submissionId)) ?? record;

    let token: string;
    try {
      token = await this.#deps.turnstile.execute();
    } catch {
      // The challenge itself did not complete. The record stays retryable and
      // the participant gets the M06 "verify and resend" affordance (§16).
      await this.#deps.outbox.markFailed(sending.submissionId, 'TURNSTILE_CLIENT');
      return { kind: 'verify_retry', record: { ...sending, status: 'pending' } };
    }

    const request = CheckinRequestSchema.safeParse(toCheckinRequest(sending, token));
    if (!request.success) {
      // The local record cannot produce a valid request, so retrying is
      // pointless — fail it here rather than making the API say so.
      await this.#deps.outbox.markFailed(sending.submissionId, 'INVALID_REQUEST');
      return {
        kind: 'rejected',
        record: { ...sending, status: 'failed' },
        code: 'INVALID_REQUEST',
        message: errorMessageVi('INVALID_REQUEST'),
      };
    }

    const outcome = await this.#deps.submit(request.data);

    if (outcome.kind === 'accepted') {
      await this.#deps.outbox.markAccepted(sending.submissionId);
      return { kind: 'accepted', record: { ...sending, status: 'accepted' } };
    }

    await this.#deps.outbox.markFailed(sending.submissionId, outcome.code);

    if (outcome.code === 'TURNSTILE_FAILED') {
      // §7.6 / §16 — the token failed, the submission did not. Reset so the
      // next attempt carries a token Siteverify has never seen.
      this.#deps.turnstile.reset();
      return { kind: 'verify_retry', record: { ...sending, status: 'pending' } };
    }

    if (!isRetryableErrorCode(outcome.code)) {
      return {
        kind: 'rejected',
        record: { ...sending, status: 'failed' },
        code: outcome.code,
        message: outcome.message,
      };
    }

    return {
      kind: 'pending',
      record: { ...sending, status: 'pending' },
      ...(outcome.retryAfterSeconds !== undefined
        ? { retryAfterSeconds: outcome.retryAfterSeconds }
        : {}),
    };
  }
}
